/**
 * Geometry and unit maths for resizing an image inside RichTextEditor.
 *
 * Everything here is pure — it either computes numbers or writes a style onto an
 * element it was handed. No React, so the arithmetic is unit-testable on its own.
 *
 * Width is always stored as a PERCENT of the image's containing block. The editor
 * renders inside a drawer while the User Guide reader renders into an article
 * column of a different width, so a px width authored in one is wrong in the
 * other by construction, and does not shrink on a narrow screen. A percent is
 * width-agnostic and means the same thing in both places.
 */

export const MIN_PX = 40;
export const MAX_PERCENT = 100;
export const ABSOLUTE_MIN_PERCENT = 4;

export const PRESETS = [25, 50, 75, 100];
export const NUDGE_STEP = 5;
export const FINE_NUDGE_STEP = 1;
export const SNAP_STEP = 5;

/** Handle size in px; offsets below are half of it, so handles straddle the edge. */
export const HANDLE_SIZE = 10;

/**
 * Six handles. No north/south: height is always `auto`, so a vertical-only drag
 * would have nothing to control and an aspect-breaking handle would be a lie.
 * `dirX` flips the drag delta for the west-side handles.
 */
export const HANDLES = [
  { key: 'nw', dirX: -1, cursor: 'nwse-resize', position: { top: -5, left: -5 } },
  { key: 'ne', dirX: 1, cursor: 'nesw-resize', position: { top: -5, right: -5 } },
  { key: 'w', dirX: -1, cursor: 'ew-resize', position: { top: '50%', left: -5, marginTop: -5 } },
  { key: 'e', dirX: 1, cursor: 'ew-resize', position: { top: '50%', right: -5, marginTop: -5 } },
  { key: 'sw', dirX: -1, cursor: 'nesw-resize', position: { bottom: -5, left: -5 } },
  { key: 'se', dirX: 1, cursor: 'nwse-resize', position: { bottom: -5, right: -5 } },
];

export const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

// ── Containing block ────────────────────────────────────────────────────────

/** Displays that establish a containing block for a normal-flow child. */
const BLOCK_DISPLAYS = /^(block|list-item|flow-root|table-cell|flex|grid|inline-block|inline-flex|inline-grid)$/;

const contentBoxWidth = (el) => {
  if (!el) return 0;
  const cs = window.getComputedStyle(el);
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  return Math.max(0, el.clientWidth - pad);
};

/**
 * The width a CSS percent resolves against — the image's nearest block ancestor,
 * not the editor. Using `editorEl.clientWidth` raw would count the editor's own
 * horizontal padding and make every image a few percent too small.
 */
export const getContainingBlockWidth = (img, editorEl) => {
  if (!img) return contentBoxWidth(editorEl);

  let node = img.parentElement;
  while (node && node !== editorEl) {
    if (BLOCK_DISPLAYS.test(window.getComputedStyle(node).display)) return contentBoxWidth(node);
    node = node.parentElement;
  }
  return contentBoxWidth(editorEl);
};

// ── Percent maths ───────────────────────────────────────────────────────────

/** Floor for the drag, so an image can never be shrunk to an unclickable speck. */
export const minPercentFor = (cbWidth) => (
  cbWidth > 0
    ? clamp(Math.ceil((MIN_PX / cbWidth) * 100), ABSOLUTE_MIN_PERCENT, MAX_PERCENT)
    : ABSOLUTE_MIN_PERCENT
);

export const pxToPercent = (px, cbWidth) => (cbWidth > 0 ? (px / cbWidth) * 100 : MAX_PERCENT);

export const clampPercent = (pct, minPct = ABSOLUTE_MIN_PERCENT) => (
  Number.isFinite(pct) ? clamp(Math.round(pct), minPct, MAX_PERCENT) : null
);

export const snapPercent = (pct, step = SNAP_STEP) => Math.round(pct / step) * step;

/**
 * The width currently set on the image, as a percent — converting a px width from
 * the pre-percent era so the toolbar reads sensibly before the image is touched.
 * `null` means no inline width at all (natural size).
 */
export const readEffectiveWidthPercent = (img, cbWidth) => {
  const raw = img?.style?.width;
  if (!raw) return null;

  const value = parseFloat(raw);
  if (!Number.isFinite(value)) return null;
  if (raw.trim().endsWith('%')) return clampPercent(value);
  return clampPercent(pxToPercent(value, cbWidth));
};

// ── Writes ──────────────────────────────────────────────────────────────────
//
// These mutate the element's inline style directly. In a contentEditable editor
// the DOM *is* the document model — React does not own this subtree — so there is
// no immutable alternative here. React state stays immutable everywhere else.

/** `pct == null` clears the inline width, falling back to the natural size. */
export const applyImageWidth = (img, pct) => {
  if (!img) return;
  if (pct == null) {
    img.style.removeProperty('width');
    img.style.removeProperty('height');
    return;
  }
  img.style.width = `${pct}%`;
  img.style.height = 'auto';
};

export const applyImageAlign = (img, mode) => {
  if (!img) return;
  img.style.removeProperty('float');
  img.style.display = '';
  img.style.margin = '0.4rem 0';

  if (mode === 'left') {
    img.style.float = 'left';
    img.style.margin = '0.4rem 1rem 0.4rem 0';
  } else if (mode === 'right') {
    img.style.float = 'right';
    img.style.margin = '0.4rem 0 0.4rem 1rem';
  } else if (mode === 'center') {
    img.style.display = 'block';
    img.style.margin = '0.6rem auto';
  }
};

export const readImageAlign = (img) => {
  if (!img) return null;
  const float = img.style.float;
  if (float === 'left' || float === 'right') return float;
  if (img.style.display === 'block' && img.style.marginLeft === 'auto') return 'center';
  return null;
};

/**
 * Convert a legacy px width to percent. Called at the top of every mutating
 * action rather than when a guide opens: rewriting on open would mark every guide
 * dirty just for being looked at.
 */
export const normalizeInlineWidth = (img, cbWidth) => {
  const raw = img?.style?.width;
  if (!raw || raw.trim().endsWith('%')) return;

  const pct = readEffectiveWidthPercent(img, cbWidth);
  if (pct != null) applyImageWidth(img, pct);
};

// ── Boxes ───────────────────────────────────────────────────────────────────

/** Box of `el` in `containerEl` coordinates, or null if it is no longer inside. */
export const measureBox = (el, containerEl) => {
  if (!el || !containerEl || !containerEl.contains(el)) return null;

  const cr = containerEl.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return { top: r.top - cr.top, left: r.left - cr.left, width: r.width, height: r.height };
};

/**
 * The scrolling editor box, in container coordinates. `clientWidth/Height`
 * excludes the scrollbar, so the overlay never ends up underneath it.
 */
export const measureViewport = (editorEl, containerEl) => {
  if (!editorEl || !containerEl) return null;

  const cr = containerEl.getBoundingClientRect();
  const er = editorEl.getBoundingClientRect();
  return {
    top: er.top - cr.top + editorEl.clientTop,
    left: er.left - cr.left + editorEl.clientLeft,
    width: editorEl.clientWidth,
    height: editorEl.clientHeight,
  };
};

/** Shallow compare so a scroll that moves nothing does not force a re-render. */
export const sameBox = (a, b) => (
  !!a && !!b
  && a.top === b.top && a.left === b.left
  && a.width === b.width && a.height === b.height
);

export const intersects = (box, viewport) => (
  !!box && !!viewport
  && box.top < viewport.top + viewport.height
  && box.top + box.height > viewport.top
  && box.left < viewport.left + viewport.width
  && box.left + box.width > viewport.left
);

/**
 * Where to put the floating toolbar, in container coordinates: above the image by
 * preference, flipped below when there is no room, and clamped inside the scroll
 * viewport as a last resort. Horizontally it is always clamped, which is what
 * keeps a ~360px toolbar on screen for a narrow or right-floated image.
 */
export const placeToolbar = ({ box, viewport, toolbar, gap = 8, edge = 4 }) => {
  if (!box || !viewport || !toolbar) return null;

  const vTop = viewport.top;
  const vBottom = viewport.top + viewport.height;
  const vLeft = viewport.left;
  const vRight = viewport.left + viewport.width;

  const above = box.top - toolbar.height - gap;
  const below = box.top + box.height + gap;

  let top;
  let placement;
  if (above >= vTop + edge) {
    top = above;
    placement = 'above';
  } else if (below + toolbar.height <= vBottom - edge) {
    top = below;
    placement = 'below';
  } else {
    // Image taller than the viewport: sit inside it, overlapping the image.
    top = clamp(above, vTop + edge, Math.max(vTop + edge, vBottom - edge - toolbar.height));
    placement = 'inside';
  }

  const left = clamp(box.left, vLeft + edge, Math.max(vLeft + edge, vRight - edge - toolbar.width));

  return { top, left, placement };
};
